import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, CheckCircle2, AlertCircle, Loader2, FilePlus2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { VIDEOS } from "@/constants/testIds";

const NONE = "__none__";
const ACCEPT = "video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm";
const ALLOWED_EXT = [".mp4", ".mov", ".m4v", ".webm"];

const bytes = (n) => {
  const mb = n / (1024 * 1024);
  return mb < 1 ? `${(n / 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`;
};

const isVideoFile = (file) => {
  const name = (file.name || "").toLowerCase();
  return ALLOWED_EXT.some((e) => name.endsWith(e)) || (file.type || "").startsWith("video/");
};

export default function BulkVideoUpload({ open, onOpenChange, accounts, onFinished }) {
  const [items, setItems] = useState([]); // {id, file, status, progress, error, videoId}
  const [accountId, setAccountId] = useState(null);
  const [caption, setCaption] = useState("");
  const [running, setRunning] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const nextIdRef = useRef(0);

  const reset = () => {
    setItems([]);
    setCaption("");
    setAccountId(null);
    setRunning(false);
    setDragOver(false);
  };

  const closeDialog = (v) => {
    if (running) return; // don't close mid-upload
    if (!v) reset();
    onOpenChange(v);
  };

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    const accepted = arr.filter(isVideoFile);
    const rejected = arr.length - accepted.length;
    if (rejected > 0) toast.error(`Пропущено файлов: ${rejected}`);
    if (accepted.length === 0) return;
    setItems((prev) => [
      ...prev,
      ...accepted.map((file) => ({
        id: `${nextIdRef.current++}`,
        file,
        status: "pending",
        progress: 0,
        error: null,
        videoId: null,
      })),
    ]);
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const clearAll = () => {
    setItems((prev) => prev.filter((it) => it.status === "uploading"));
  };

  const startUpload = async () => {
    if (running) return;
    const queue = items.filter((it) => it.status === "pending" || it.status === "error");
    if (queue.length === 0) {
      toast.error(t.videos.bulk.empty);
      return;
    }
    setRunning(true);
    let done = 0, failed = 0;
    for (const it of queue) {
      setItems((prev) => prev.map((p) => p.id === it.id
        ? { ...p, status: "uploading", progress: 0, error: null }
        : p));
      try {
        const created = await api.uploadVideo(
          it.file,
          {
            assigned_account_id: accountId || undefined,
            caption: caption || undefined,
          },
          (pct) => {
            setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, progress: pct } : p));
          },
        );
        done++;
        setItems((prev) => prev.map((p) => p.id === it.id
          ? { ...p, status: "done", progress: 100, videoId: created.id }
          : p));
      } catch (e) {
        failed++;
        const msg = e?.response?.data?.detail || e?.message || "upload failed";
        setItems((prev) => prev.map((p) => p.id === it.id
          ? { ...p, status: "error", error: String(msg) }
          : p));
      }
    }
    setRunning(false);
    if (done > 0 && failed === 0) toast.success(t.videos.bulk.allDone(done));
    else if (done > 0 && failed > 0) toast.warning?.(t.videos.bulk.partial(done, failed)) || toast(t.videos.bulk.partial(done, failed));
    else toast.error(t.videos.bulk.allFailed);
    if (done > 0 && typeof onFinished === "function") onFinished();
  };

  const doneCount = items.filter((i) => i.status === "done").length;
  const failedCount = items.filter((i) => i.status === "error").length;

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (running) return;
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  };

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent className="rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{t.videos.bulk.title}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-500 -mt-1">{t.videos.bulk.description}</p>

        <div
          data-testid={VIDEOS.bulkDropzone}
          onClick={() => !running && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); if (!running) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`mt-3 rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
            dragOver ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:border-emerald-300 bg-slate-50/60"
          } ${running ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          <FilePlus2 className="h-8 w-8 mx-auto text-emerald-500" />
          <div className="mt-2 text-sm font-medium text-slate-700">{t.videos.bulk.dropHere}</div>
          <div className="text-xs text-slate-500">{t.videos.bulk.dropSub}</div>
          <input
            data-testid={VIDEOS.bulkInput}
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              if (inputRef.current) inputRef.current.value = "";
            }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">{t.videos.bulk.account}</Label>
            <Select
              value={accountId || NONE}
              onValueChange={(v) => setAccountId(v === NONE ? null : v)}
              disabled={running}
            >
              <SelectTrigger data-testid={VIDEOS.bulkAccount} className="mt-1">
                <SelectValue placeholder={t.common.unlinked} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t.common.unlinked}</SelectItem>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.username}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-400 mt-1">{t.videos.bulk.accountHint}</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">{t.videos.bulk.caption}</Label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="mt-1 min-h-[70px]"
              disabled={running}
              placeholder="—"
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div data-testid={VIDEOS.bulkSummary} className="text-xs text-slate-500">
              {items.length === 0
                ? t.videos.bulk.empty
                : t.videos.bulk.summary(doneCount, failedCount, items.length)}
            </div>
            {items.length > 0 && (
              <button
                data-testid={VIDEOS.bulkClear}
                onClick={clearAll}
                disabled={running}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600 disabled:opacity-50"
              >
                <Trash2 className="h-3 w-3" /> {t.videos.bulk.clear}
              </button>
            )}
          </div>
          <div className="max-h-[240px] overflow-y-auto divide-y divide-slate-100 rounded-xl border border-slate-100 bg-white">
            {items.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-slate-400">{t.videos.bulk.empty}</div>
            ) : items.map((it, idx) => (
              <div
                key={it.id}
                data-testid={VIDEOS.bulkItem(idx)}
                className="px-3 py-2.5 flex items-center gap-3 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate font-mono text-xs text-slate-700">{it.file.name}</div>
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                    <span>{bytes(it.file.size)}</span>
                    <span data-testid={VIDEOS.bulkItemStatus(idx)}>
                      {it.status === "pending" && t.videos.bulk.statusPending}
                      {it.status === "uploading" && `${t.videos.bulk.statusUploading} ${it.progress}%`}
                      {it.status === "done" && t.videos.bulk.statusDone}
                      {it.status === "error" && `${t.videos.bulk.statusError}: ${it.error}`}
                    </span>
                  </div>
                  {it.status === "uploading" && (
                    <div className="h-1 rounded-full bg-slate-100 overflow-hidden mt-1.5">
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${it.progress}%` }} />
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  {it.status === "pending" && <span className="text-slate-300 text-xs">•</span>}
                  {it.status === "uploading" && <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />}
                  {it.status === "done" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                  {it.status === "error" && <AlertCircle className="h-4 w-4 text-rose-500" />}
                </div>
                {it.status !== "uploading" && it.status !== "done" && (
                  <button
                    data-testid={VIDEOS.bulkItemRemove(idx)}
                    onClick={() => removeItem(it.id)}
                    disabled={running}
                    className="text-slate-400 hover:text-rose-500 disabled:opacity-50"
                    aria-label={t.videos.bulk.remove}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <button
            data-testid={VIDEOS.bulkClose}
            onClick={() => closeDialog(false)}
            disabled={running}
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {t.videos.bulk.close}
          </button>
          <button
            data-testid={VIDEOS.bulkStart}
            onClick={startUpload}
            disabled={running || items.filter((it) => it.status === "pending" || it.status === "error").length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium disabled:opacity-60 transition-colors"
          >
            {running ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> {t.videos.bulk.running}
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" /> {t.videos.bulk.start}
              </>
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
